import * as styles from './index.module.scss';
import React, { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { DatePicker, Dropdown, Flex, Modal, Table } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import Clickable from '../../../../ui/Clickable';
import Icon from '../../../../ui/Icon';
import { StagesStore } from '../../../../../DataSource/Stores/Cabinet/StagesStore';
import { AuthStore } from '../../../../../DataSource/Stores/AuthStore';
import { useParams } from 'react-router-dom';
import { Statistic } from '../../../../ types';

const { RangePicker } = DatePicker;

type DateRange = 'Сегодня' | 'Вчера' | 'Неделя' | 'Месяц' | 'Выбрать свой';

const StatisticsPage: FC = () => {
  const { id: workspaceId } = useParams();
  const [dateRange, setDateRange] = useState<DateRange>('Сегодня');
  const [customDateRange, setCustomDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [averageDuration, setAverageDuration] = useState<string>('0,00');
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const user = AuthStore.currentUserData.user;

  useEffect(() => {
    const userStatistics = StagesStore.dataUserStatistic;

    if (userStatistics.length > 0) {
      const totalDuration = userStatistics.reduce((total, item) => total + item.duration, 0);
      const average = totalDuration / userStatistics.length;
      const result = (average / 60).toFixed(2).replace('.', ',');
      setAverageDuration(result);
    } else {
      setAverageDuration('0,00');
    }
  }, [StagesStore.dataUserStatistic]);

  const getDateRange = (range: string) => {
    let startDate, endDate;

    switch (range) {
      case 'Сегодня':
        startDate = dayjs().startOf('day').toDate();
        endDate = dayjs().endOf('day').toDate();
        break;

      case 'Вчера':
        startDate = dayjs().subtract(1, 'day').startOf('day').toDate();
        endDate = dayjs().subtract(1, 'day').endOf('day').toDate();
        break;

      case 'Неделя':
        startDate = dayjs().subtract(6, 'day').startOf('day').toDate();
        endDate = dayjs().endOf('day').toDate();
        break;

      case 'Месяц':
        startDate = dayjs().subtract(30, 'day').startOf('day').toDate();
        endDate = dayjs().toDate();
        break;

      case 'Выбрать свой':
        if (customDateRange && customDateRange[0] && customDateRange[1]) {
          startDate = customDateRange[0].startOf('day').toDate();
          endDate = customDateRange[1].endOf('day').toDate();
        } else {
          startDate = dayjs().toDate();
          endDate = dayjs().toDate();
        }
        break;

      default:
        startDate = dayjs().toDate();
        endDate = dayjs().toDate();
    }

    return { startDate, endDate };
  };

  const handleDateRange = (value: DateRange) => {
    if (!workspaceId) return;

    setDateRange(value);

    const { startDate, endDate } = getDateRange(value);

    StagesStore.getUserStatistic(workspaceId, user._id, startDate, endDate).catch((e) => {
      console.error(e);
    });
  };

  const handleCustomDateRange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setCustomDateRange(dates);

    if (!workspaceId || !dates || !dates[0] || !dates[1]) return;

    const startDate = dates[0].startOf('day').toDate();
    const endDate = dates[1].endOf('day').toDate();

    StagesStore.getUserStatistic(workspaceId, user._id, startDate, endDate).catch((e) => {
      console.error(e);
    });
  };

  const handleRowClick = (jobId: string) => {
    try {
      const userStatistics = StagesStore.dataUserStatistic;
      const selectedJob = userStatistics.find((item) => item.jobId === jobId);

      if (selectedJob) {
        setSelectedJob(selectedJob);
        setModalOpen(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <Flex vertical gap={24}>
        <p className={styles.title}>Статистика заказов</p>

        <Flex vertical gap={8}>
          <div className={styles.rangePickerWrap}>
            <p className={styles.name}>Диапазон:</p>
            <Dropdown
              menu={{
                items: [
                  { key: 1, label: 'Сегодня', onClick: () => handleDateRange('Сегодня') },
                  { key: 2, label: 'Вчера', onClick: () => handleDateRange('Вчера') },
                  { key: 3, label: 'Неделя', onClick: () => handleDateRange('Неделя') },
                  { key: 4, label: 'Месяц', onClick: () => handleDateRange('Месяц') },
                  {
                    key: 5,
                    label: 'Выбрать свой',
                    onClick: () => handleDateRange('Выбрать свой'),
                  },
                ],
              }}
              trigger={['click']}
            >
              <Clickable className={styles.rangePickerBtn}>
                {dateRange} <Icon slug="angle_down" color="#F1B729" />
              </Clickable>
            </Dropdown>
          </div>
          <div className={styles.rangeBlock}>
            {dateRange === 'Сегодня' && dayjs().format('DD / MM / YYYY')}
            {dateRange === 'Вчера' && dayjs().subtract(1, 'day').format('DD / MM / YYYY')}
            {dateRange === 'Неделя' && (
              <>
                {dayjs().subtract(6, 'day').format('DD / MM / YYYY')}
                <span>—</span>
                {dayjs().format('DD / MM / YYYY')}
              </>
            )}
            {dateRange === 'Месяц' && (
              <>
                {dayjs().subtract(30, 'day').format('DD / MM / YYYY')}
                <span>—</span>
                {dayjs().format('DD / MM / YYYY')}
              </>
            )}
            {dateRange === 'Выбрать свой' && (
              <RangePicker
                value={customDateRange}
                onChange={(dates) => handleCustomDateRange(dates)}
              />
            )}
          </div>
        </Flex>

        <Flex vertical gap={12}>
          <Flex align="center" gap={8}>
            <p className={styles.label}>Выполнено (шт.):</p>
            <p className={styles.value}>{StagesStore.dataUserStatistic.length}</p>
          </Flex>

          <Flex align="center" gap={8}>
            <p className={styles.label}>Среднее время (мин.):</p>
            <p className={styles.value}>{averageDuration}</p>
          </Flex>
        </Flex>

        <Table
          className={styles.table}
          dataSource={StagesStore.dataUserStatistic}
          pagination={false}
          showSorterTooltip={{ target: 'sorter-icon' }}
          scroll={{ y: 480 }}
          rowKey={(record) => `${record.jobId}`}
          onRow={(record) => ({
            onClick: () => handleRowClick(record.jobId || ''),
          })}
          columns={[
            {
              key: 'bitrixId',
              dataIndex: 'bitrixId',
              title: 'BitrixID',
              width: '25%',
              render: (_, record: Statistic) => <p>{record.externalId?.bitrixId}</p>,
            },

            {
              key: 'createdAt',
              dataIndex: 'createdAt',
              title: 'Дата и Время',
              render: (_, record: Statistic) => (
                <p>{dayjs(record.createdAt).format('DD/MM/YYYY HH:mm')}</p>
              ),
            },
            {
              key: 'duration',
              dataIndex: 'duration',
              title: 'Длительность',
              render: (_, record: Statistic) => (
                <p>{Math.round((record.duration / 60) * 100) / 100} мин.</p>
              ),
              sorter: (a, b) => a.duration / 60 - b.duration / 2,
            },
          ]}
        />
      </Flex>

      <Modal open={modalOpen} title="Детали" footer={null} onCancel={() => setModalOpen(false)}>
        <Flex vertical gap={8}>
          <p>jobId: {selectedJob?.jobId}</p>
          <p>bitrixId: {selectedJob?.externalId?.bitrixId}</p>
          <p>
            Продолжительность:{' '}
            {selectedJob?.duration ? Math.round((selectedJob.duration / 60) * 100) / 100 : '-'} мин
          </p>
          <p>Создано: {dayjs(selectedJob?.createdAt).format('DD/MM/YYYY HH:mm')}</p>
          <p>Описание: {selectedJob?.jobData[0].text}</p>
        </Flex>
      </Modal>
    </>
  );
};

export default observer(StatisticsPage);
